<template>
    <q-layout view="hHh lpR fFf">
        <marketing-nav :mobile-nav-open="mobileNav" @toggle-mobile="mobileNav = !mobileNav" />

        <q-page-container>
            <!-- Hero -->
            <section class="mkt-hero-light" style="padding:60px 0 32px;">
                <div class="mkt-container text-center">
                    <div v-if="currentTag" class="text-uppercase text-weight-bold q-mb-sm" style="font-size:0.8rem;letter-spacing:0.1em;color:#4F46E5;">
                        Tag
                    </div>
                    <h1 class="text-weight-bold q-mb-sm" style="font-size:clamp(2rem,4vw,2.6rem);color:#0F172A;">
                        {{ currentTag ? currentTag : 'News &amp; Insights' }}
                    </h1>
                    <p class="text-grey-7 q-mx-auto" style="max-width:640px;font-size:1.05rem;line-height:1.6;">
                        <template v-if="currentTag">
                            Posts tagged "{{ currentTag }}". <a href="/news" class="text-primary text-no-decoration">View all news &rsaquo;</a>
                        </template>
                        <template v-else>
                            Product news, changelog entries, and case studies from the eSign Launchpad team.
                        </template>
                    </p>
                </div>
            </section>

            <!-- Posts grid -->
            <section class="reveal-fade-up" v-intersection="revealCfg" style="padding:24px 0 64px;">
                <div class="mkt-container">
                    <div v-if="!posts.length" class="text-center q-py-xl">
                        <q-icon name="article" size="48px" color="grey-5" />
                        <div class="text-h6 q-mt-md text-grey-7">No posts published yet.</div>
                        <p class="text-grey-6 q-mt-sm">
                            Check back soon, or
                            <a href="/news/rss" class="text-primary text-no-decoration">subscribe to the RSS feed</a>.
                        </p>
                    </div>

                    <div v-else class="row q-col-gutter-lg">
                        <div v-for="post in posts" :key="post.id" class="col-12 col-sm-6 col-md-4">
                            <a :href="`/news/${post.slug}`" class="news-card">
                                <div class="news-card__image" :style="cardImageStyle(post)">
                                    <div v-if="!post.featuredImage" class="news-card__placeholder">
                                        <q-icon name="article" size="40px" color="grey-4" />
                                    </div>
                                </div>
                                <div class="news-card__body">
                                    <div class="news-card__meta">
                                        <span v-if="post.publishedDate">{{ formatDate(post.publishedDate) }}</span>
                                        <span v-if="post.publishedDate &amp;&amp; post.author">&middot;</span>
                                        <span v-if="post.author">{{ post.author }}</span>
                                    </div>
                                    <h3 class="news-card__title">{{ post.title }}</h3>
                                    <p v-if="post.metaDescription" class="news-card__excerpt">{{ post.metaDescription }}</p>
                                    <div v-if="postTags(post).length" class="news-card__tags">
                                        <span v-for="tag in postTags(post)" :key="tag" class="news-card__tag">{{ tag }}</span>
                                    </div>
                                </div>
                            </a>
                        </div>
                    </div>

                    <!-- Pagination -->
                    <div v-if="totalPages &gt; 1" class="row justify-center q-mt-xl">
                        <q-pagination
                            v-model="currentPage"
                            :max="totalPages"
                            :max-pages="7"
                            direction-links
                            boundary-links
                            color="primary"
                            @update:model-value="goToPage"
                        />
                    </div>
                </div>
            </section>

            <!-- RSS hint -->
            <section v-if="posts.length" v-intersection="revealCfg" class="reveal-fade-in" style="padding:0 0 64px;">
                <div class="mkt-container text-center">
                    <a href="/news/rss" class="text-primary text-no-decoration" style="font-size:0.9rem;">
                        <q-icon name="rss_feed" size="14px" /> Subscribe via RSS
                    </a>
                </div>
            </section>

            <marketing-footer />
        </q-page-container>
    </q-layout>
</template>

<script>
import MarketingNav from '@components/marketing/MarketingNav.vue';
import MarketingFooter from '@components/marketing/MarketingFooter.vue';
import { revealMixin } from '@util/reveal.js';

export default {
    name: 'News',
    mixins: [revealMixin],
    components: { MarketingNav, MarketingFooter },
    data() {
        return {
            mobileNav: false,
            // Server-injected via News.cshtml HeaderScripts. Defaults are
            // defensive so the component still renders if the script tag
            // is missing (e.g., during a hot-reload edge case).
            posts: window._contentPosts || [],
            currentPage: window._currentPage || 1,
            totalPages: window._totalPages || 1,
            currentTag: window._currentTag || null,
            currentTagSlug: window._currentTagSlug || null
        };
    },
    methods: {
        formatDate(d) {
            if (!d) return '';
            const dt = new Date(d);
            return dt.toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        },
        postTags(post) {
            if (!post.tags) return [];
            return post.tags
                .split(',')
                .map(t => t.trim())
                .filter(Boolean)
                .slice(0, 3); // cap to keep card visually tidy
        },
        cardImageStyle(post) {
            if (post.featuredImage) {
                return { backgroundImage: `url('${post.featuredImage}')` };
            }
            return {};
        },
        goToPage(page) {
            // Server-rendered pagination - full navigation, not SPA.
            const base = this.currentTagSlug
                ? `/news/tag/${encodeURIComponent(this.currentTagSlug)}`
                : '/news';
            window.location.href = page === 1 ? base : `${base}/page/${page}`;
        }
    }
};
</script>

<style scoped>
.news-card {
    display: flex;
    flex-direction: column;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    overflow: hidden;
    text-decoration: none;
    color: #0F172A;
    transition: transform 0.18s ease-out, box-shadow 0.18s ease-out, border-color 0.18s ease-out;
    height: 100%;
}
.news-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 30px rgba(15, 23, 42, 0.08);
    border-color: #c7d2fe;
}
.news-card__image {
    height: 200px;
    background-size: cover;
    background-position: center;
    background-color: #f1f5f9;
    position: relative;
}
.news-card__placeholder {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
}
.news-card__body {
    padding: 18px 20px 22px;
    display: flex;
    flex-direction: column;
    flex: 1;
}
.news-card__meta {
    font-size: 0.78rem;
    color: #64748b;
    margin-bottom: 8px;
}
.news-card__meta > span + span {
    margin-left: 6px;
}
.news-card__title {
    font-size: 1.1rem;
    font-weight: 600;
    line-height: 1.35;
    margin: 0 0 8px;
    color: #0F172A;
}
.news-card__excerpt {
    font-size: 0.9rem;
    color: #475569;
    line-height: 1.55;
    margin: 0 0 12px;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
.news-card__tags {
    margin-top: auto;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}
.news-card__tag {
    font-size: 0.72rem;
    color: #4f46e5;
    background: #eef2ff;
    border-radius: 4px;
    padding: 2px 8px;
    font-weight: 500;
}
</style>
